import { forwardRef, useCallback } from "react"

// --- Lib ---
import { parseShortcutKeys } from "@/tiptap/lib/tiptap-utils"

// --- Hooks ---
import { useTiptapEditor } from "@/tiptap/hooks/use-tiptap-editor"

import {
  IMAGE_DOWNLOAD_SHORTCUT_KEY,
  useImageDownload,
} from "@/tiptap/ui/image-download-button"

import { Button } from "@/tiptap/ui-primitive/button"
import { Badge } from "@/tiptap/ui-primitive/badge"

export function ImageDownloadShortcutBadge({
  shortcutKeys = IMAGE_DOWNLOAD_SHORTCUT_KEY
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>;
}

/**
 * Button component for downloading images from a Tiptap editor.
 * Only appears when an image is selected in the editor.
 *
 * For custom button implementations, use the `useImageDownload` hook instead.
 */
export const ImageDownloadButton = forwardRef((
  {
    editor: providedEditor,
    text,
    hideWhenUnavailable = false,
    onDownloaded,
    resolveFileUrl,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    canDownload,
    handleDownload,
    label,
    shortcutKeys,
    Icon,
  } = useImageDownload({
    editor,
    hideWhenUnavailable,
    onDownloaded,
    resolveFileUrl,
  })

  const handleClick = useCallback(async (event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    await handleDownload()
  }, [handleDownload, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type="button"
      variant="ghost"
      data-active-state="off"
      role="button"
      tabIndex={-1}
      disabled={!canDownload}
      data-disabled={!canDownload}
      aria-label={label}
      tooltip={label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}>
      {children ?? (
        <>
          <Icon className="tiptap-button-icon" />
          {text && <span className="tiptap-button-text">{text}</span>}
          {showShortcut && (
            <ImageDownloadShortcutBadge shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  );
})

ImageDownloadButton.displayName = "ImageDownloadButton"
